import { register } from 'ol/proj/proj4';
import proj4 from 'proj4';

/**
 * OpenLayers supporta nativamente solo EPSG:4326 ed EPSG:3857 e, per
 * scelta di design, non include alcun database EPSG (nemmeno proj4js
 * ne imbarca uno): ogni altro CRS va registrato esplicitamente con la
 * sua definizione proj4.
 *
 * Qui sono raccolti i CRS piani e geografici più diffusi nei servizi
 * geografici (WFS/WMS) della PA italiana. Le definizioni provengono
 * dal registro EPSG (consultabile su https://epsg.io/<codice>): per
 * aggiungere un CRS copiare da lì la stringa proj4.
 *
 * Nota sull'ordine degli assi: le stringhe proj4 non lo codificano e i
 * CRS registrati risultano sempre Est-Nord (lon/lat). Per questo NON
 * sono inclusi EPSG:6707/6708 (RDN2008 / TM32-TM33), che hanno ordine
 * ufficiale Nord-Est: un GML in quei CRS verrebbe letto con le
 * coordinate invertite. Aggiungerli solo gestendo esplicitamente gli
 * assi.
 */
const ITALIAN_PROJECTION_DEFS: Record<string, string> = {
  // ETRS89 / UTM zone 32N-33N — standard de facto dei servizi
  // regionali (es. Emilia-Romagna).
  'EPSG:25832':
    '+proj=utm +zone=32 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs',
  'EPSG:25833':
    '+proj=utm +zone=33 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs',
  // WGS 84 / UTM zone 32N-33N — comune nei servizi nazionali.
  'EPSG:32632': '+proj=utm +zone=32 +datum=WGS84 +units=m +no_defs',
  'EPSG:32633': '+proj=utm +zone=33 +datum=WGS84 +units=m +no_defs',
  // Monte Mario / Italy zone 1-2 (Gauss-Boaga) — cartografia storica,
  // catasto e molti servizi comunali. I parametri towgs84 sono la
  // trasformazione media ufficiale verso WGS84: accuratezza metrica,
  // adeguata alla visualizzazione su mappa.
  'EPSG:3003':
    '+proj=tmerc +lat_0=0 +lon_0=9 +k=0.9996 +x_0=1500000 +y_0=0 +ellps=intl ' +
    '+towgs84=-104.1,-49.1,-9.9,0.971,-2.917,0.714,-11.68 +units=m +no_defs',
  'EPSG:3004':
    '+proj=tmerc +lat_0=0 +lon_0=15 +k=0.9996 +x_0=2520000 +y_0=0 +ellps=intl ' +
    '+towgs84=-104.1,-49.1,-9.9,0.971,-2.917,0.714,-11.68 +units=m +no_defs',
  // RDN2008 (realizzazione italiana di ETRF2000) — sistema geodetico
  // ufficiale nazionale dal DM 10/11/2011, usato dai geoportali più
  // recenti. Alla precisione di proj4 coincide con ETRS89: cambiano
  // solo i codici EPSG (6706 geografico, 7791/7792 UTM 32N/33N).
  'EPSG:6706': '+proj=longlat +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +no_defs',
  'EPSG:7791':
    '+proj=utm +zone=32 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs',
  'EPSG:7792':
    '+proj=utm +zone=33 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs',
};

let registered = false;

/**
 * Registra in OpenLayers (via proj4) i CRS italiani comuni, così
 * `readFeatures` può riproiettare risposte espresse in un CRS diverso
 * da quello della mappa. Idempotente: registra una sola volta.
 */
export function registerItalianProjections(): void {
  if (registered) return;

  for (const [code, definition] of Object.entries(ITALIAN_PROJECTION_DEFS)) {
    proj4.defs(code, definition);
  }
  register(proj4);
  registered = true;
}
